import { captureIdentity, getActiveUserId, registerIdentityStorage } from '@/api/identity'

// «Входящие» для файла из «Поделиться»: service worker кладёт сюда PDF (src/sw/modules/share.ts),
// экран импорта забирает его и загружает обычным запросом. Хранилище — Cache Storage: оно общее
// для SW и страницы и переживает перезагрузку, блокировку приложения и вход.
// Файл лежит, пока загрузка не удалась: экран читает его `peekSharedFile` и убирает
// `clearSharedFile` только после ответа сервера. Здесь нет React: модуль попадает в SW.

export const SHARE_INBOX_CACHE = 'share-inbox-v2'
export const SHARE_INBOX_KEY = '/share-inbox/inbody'

const NAME_HEADER = 'X-File-Name'
const FALLBACK_NAME = 'inbody.pdf'

function storage(): CacheStorage | null {
  return typeof caches === 'undefined' ? null : caches
}

/** Положить файл во «входящие»; прежний файл заменяется (ждёт всегда один). */
export async function putSharedFile(file: File, owner = getActiveUserId()): Promise<void> {
  if (!owner) return
  const store = storage()
  if (!store) return
  const cache = await store.open(`${SHARE_INBOX_CACHE}:${owner}`)
  await cache.put(
    SHARE_INBOX_KEY,
    new Response(file, {
      headers: {
        'Content-Type': file.type || 'application/pdf',
        [NAME_HEADER]: encodeURIComponent(file.name || FALLBACK_NAME),
      },
    }),
  )
}

/** Файл из «входящих», не убирая его оттуда; нет файла — null. */
export async function peekSharedFile(): Promise<File | null> {
  const identity = captureIdentity()
  const owner = identity.id
  if (!owner) return null
  const store = storage()
  if (!store) return null
  const cache = await store.open(`${SHARE_INBOX_CACHE}:${owner}`)
  const response = await cache.match(SHARE_INBOX_KEY)
  if (!response) return null
  const blob = await response.blob()
  if (!identity.isCurrent()) return null
  const name = decodeName(response.headers.get(NAME_HEADER))
  return new File([blob], name, { type: response.headers.get('Content-Type') ?? 'application/pdf' })
}

function decodeName(raw: string | null): string {
  try {
    return raw ? decodeURIComponent(raw) : FALLBACK_NAME
  } catch {
    return FALLBACK_NAME
  }
}

/** Убрать файл из «входящих» (после того как сервер его принял или отказал). */
export async function clearSharedFile(): Promise<void> {
  const owner = getActiveUserId()
  if (!owner) return
  const store = storage()
  if (!store) return
  const cache = await store.open(`${SHARE_INBOX_CACHE}:${owner}`)
  await cache.delete(SHARE_INBOX_KEY)
}

/** Забрать файл: прочитать и убрать. */
export async function takeSharedFile(): Promise<File | null> {
  const identity = captureIdentity()
  const file = await peekSharedFile()
  if (!identity.isCurrent()) return null
  if (file && identity.isCurrent()) await clearSharedFile()
  if (!identity.isCurrent()) return null
  return file
}

registerIdentityStorage(async (owner) => {
  await storage()?.delete('share-inbox-v1')
  if (owner) await storage()?.delete(`${SHARE_INBOX_CACHE}:${owner}`)
})
